// Import necessary hooks and modules from React and other libraries
import { useState, useEffect } from 'react';
import axios from 'axios';
import type { Feedback } from '../types'; // Type definition for Feedback object
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Functional component to allow teachers to view feedback for a specific course
function ViewFeedbackPage() {
  // Get authenticated user and loading state from custom hook
  const { user, isLoading } = useAuth();
  // Get course_id from the URL parameters
  const { course_id } = useParams<{ course_id: string }>();
  // State for storing feedback data
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  // State for storing error messages
  const [error, setError] = useState<string | null>(null);
  // State for loading status of feedback fetch
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  // React Router navigation function
  const navigate = useNavigate();
  // useEffect to fetch feedback once the component mounts and user is loaded
  useEffect(() => {
      // If authentication is still loading, return early
    if (isLoading) return;
    // Redirect to login if no user or user is not a teacher
    if (!user || user.role !== 'teacher') {
      navigate('/login');
      return;
    }
    // Async function to fetch feedback data from API
    const fetchFeedback = async () => {
      setIsLoadingFeedback(true); // Start feedback loading state
      try {
        // Fetch CSRF token to ensure secure API requests
        await axios.get('/elearning/get-csrf/', { withCredentials: true });
        // Fetch feedback for the specified course
        const response = await axios.get(`/elearning/course/view_feedback/${course_id}/`, {
          withCredentials: true,
        });
        // Update state
        setFeedbacks(response.data.feedbacks || []);
        setError(null); // Clear any previous error
      } catch (err: any) {
        // Handle errors while fetching feedback
        const errorMessage = err.response?.data?.message
          ? JSON.stringify(err.response.data.message, null, 2)
          : err.message || 'Failed to fetch feedback';
        console.error('ViewFeedbackPage: Error fetching feedback=', errorMessage, 'status=', err.response?.status, 'data=', JSON.stringify(err.response?.data, null, 2));
        setError(errorMessage);
      } finally {
        setIsLoadingFeedback(false); // Stop loading spinner
      }
    };
    // Call the async fetch function
    fetchFeedback();
  }, [user, isLoading, course_id, navigate]);
  // Show global loading spinner if user authentication is still loading
  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  return (
  <div className="min-h-screen bg-gray-50 flex justify-center py-12 px-4 sm:px-6 lg:px-8">
    <div className="w-full max-w-2xl">
      {/* Header with page title and back link */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">View Feedback</h1>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back to Homepage
        </button>
      </div>

      {/* Display error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg shadow-sm">
          {error}
        </div>
      )}

      {/* Loading spinner */}
      {isLoadingFeedback ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-600"></div>
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="bg-white p-6 rounded-2xl shadow-md text-center">
          <p className="text-gray-500 text-lg">No feedback available for this course.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbacks.map((feedback) => (
            <div
              key={feedback.id}
              className="bg-white p-5 rounded-2xl shadow-md hover:shadow-lg transition-shadow"
            >
              <p className="text-gray-800 font-medium mb-1">
                Course: {feedback.course?.name || 'Unknown Course'}
              </p>
              <p className="text-gray-700 mb-2">
                Student: {feedback.student?.username || 'Unknown Student'}
              </p>
              <p className="text-gray-700">{feedback.content || 'No content'}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
}
// Export the component for use in routes
export default ViewFeedbackPage;
